import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { dbOperations } from '../../utils/supabase'
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'

const AddProductSetPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    set_price: '',
    discount_amount: '',
    category: '',
    is_active: true,
    is_featured: false,
    tags: ''
  })

  // Image handling
  const [uploadedImages, setUploadedImages] = useState([])
  const [uploadingImages, setUploadingImages] = useState(false)

  // Set items
  const [setItems, setSetItems] = useState([])
  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedQuantity, setSelectedQuantity] = useState('1')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [productsResponse, categoriesResponse] = await Promise.all([
        dbOperations.products.getAll(),
        dbOperations.categories.getAll()
      ])
      
      setProducts(productsResponse.data || [])
      setCategories(categoriesResponse.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Image handling functions
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    
    if (files.length === 0) return
    
    // Direkt yükleme yap
    setUploadingImages(true)
    try {
      const uploadPromises = files.map(file => 
        dbOperations.storage.uploadImage(file)
      )
      
      const results = await Promise.all(uploadPromises)
      
      const successfulUploads = results
        .filter(result => !result.error)
        .map(result => ({
          url: result.url,
          fileName: result.fileName
        }))

      setUploadedImages(prev => [...prev, ...successfulUploads])
      console.log('Images uploaded successfully:', successfulUploads)
    } catch (error) {
      console.error('Error uploading images:', error)
      alert('Resim yüklenirken hata oluştu!')
    } finally {
      setUploadingImages(false)
    }
  }



  const removeUploadedImage = async (index) => {
    const imageToRemove = uploadedImages[index]
    try {
      if (imageToRemove.fileName) {
        await dbOperations.storage.deleteFile(imageToRemove.fileName)
      }
      setUploadedImages(uploadedImages.filter((_, i) => i !== index))
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

  // Bu fonksiyon artık kullanılmıyor - handleFileSelect direkt yükleme yapıyor

  // Drag & Drop functions
  const [draggedIndex, setDraggedIndex] = useState(null)

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      return
    }

    const newImages = [...uploadedImages]
    const draggedImage = newImages[draggedIndex]
    
    // Remove dragged item
    newImages.splice(draggedIndex, 1)
    
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage)
    
    setUploadedImages(newImages)
    setDraggedIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const moveImageUp = (index) => {
    if (index === 0) return
    const newImages = [...uploadedImages]
    const temp = newImages[index]
    newImages[index] = newImages[index - 1]
    newImages[index - 1] = temp
    setUploadedImages(newImages)
  }

  const moveImageDown = (index) => {
    if (index === uploadedImages.length - 1) return
    const newImages = [...uploadedImages]
    const temp = newImages[index]
    newImages[index] = newImages[index + 1]
    newImages[index + 1] = temp
    setUploadedImages(newImages)
  }

  // Set items functions
  const addSetItem = () => {
    if (!selectedProduct) return

    const existingItem = setItems.find(item => item.product_id === selectedProduct)
    const product = products.find(p => p.id === selectedProduct)
    
    if (existingItem) {
      setSetItems(setItems.map(item => 
        item.product_id === selectedProduct 
          ? { ...item, quantity: item.quantity + parseInt(selectedQuantity) }
          : item
      ))
    } else {
      setSetItems([...setItems, {
        product_id: selectedProduct,
        quantity: parseInt(selectedQuantity),
        product_name: product?.name,
        product_price: product?.price
      }])
    }

    setSelectedProduct('')
    setSelectedQuantity('1')
  }

  const removeSetItem = (productId) => {
    setSetItems(setItems.filter(item => item.product_id !== productId))
  }

  const updateItemQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return
    setSetItems(setItems.map(item => 
      item.product_id === productId 
        ? { ...item, quantity: parseInt(newQuantity) }
        : item
    ))
  }

  const calculateTotalPrice = () => {
    return setItems.reduce((total, item) => {
      const product = products.find(p => p.id === item.product_id)
      return total + (product?.price || 0) * item.quantity
    }, 0)
  }

  const calculateDiscountedPrice = () => {
    const total = calculateTotalPrice()
    const discount = parseFloat(formData.discount_amount) || 0
    return Math.max(0, total - discount)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Calculate final price
      let finalPrice = parseFloat(formData.set_price)
      if (!finalPrice) {
        finalPrice = calculateDiscountedPrice()
      }

      const setData = {
        ...formData,
        set_price: finalPrice,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        image_url: uploadedImages.length > 0 ? uploadedImages[0].url : '',
        // Çoklu görsel desteği - images array olarak kaydet
        images: uploadedImages.map((img, index) => ({
          url: img.url,
          fileName: img.fileName,
          is_main: index === 0, // İlk görsel ana görsel
          sort_order: index
        }))
      }

      // Create the set
      const { data: newSet, error: setError } = await dbOperations.productSets.create(setData)
      
      if (setError) throw setError

      // Add items to the set
      for (const item of setItems) {
        const product = products.find(p => p.id === item.product_id)
        await dbOperations.productSetItems.create({
          set_id: newSet.id,
          product_id: item.product_id,
          quantity: item.quantity,
          price_per_unit: product?.price || 0
        })
      }

      alert('Takım başarıyla oluşturuldu!')
      navigate('/admin/product-sets')
    } catch (error) {
      console.error('Error creating set:', error)
      alert('Takım oluşturulurken hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/product-sets')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🎯 Yeni Takım Oluştur</h1>
            <p className="mt-1 text-sm text-gray-600">
              Ürün takımı oluşturun ve içeriğini belirleyin
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">📝 Temel Bilgiler</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Takım Adı *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Örnek: Semih Koltuk Takımı"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Açıklama
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Takım hakkında açıklama..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Kategori *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Kategori Seçin</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Etiketler
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="virgül ile ayırın"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Aktif
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
                Anasayfada göster
              </label>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">📸 Takım Görselleri</h2>
          
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Görsel Yükle {uploadingImages && <span className="text-indigo-600">(Yükleniyor...)</span>}
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                  >
                    <span>Dosya seçin</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={uploadingImages}
                    />
                  </label>
                  <p className="pl-1">veya sürükle bırak</p>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, GIF 10MB'a kadar</p>
                <p className="text-xs text-indigo-500">Seçtikten hemen sonra otomatik yüklenir</p>
              </div>
            </div>
          </div>

          {/* Uploaded Images */}
          {uploadedImages.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                📸 Yüklenmiş Görseller 
                <span className="text-xs text-gray-500 ml-1">
                  (Sürükleyerek sıralayabilirsiniz)
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadedImages.map((image, index) => (
                  <div 
                    key={index} 
                    className={`relative group cursor-move transition-all duration-200 ${
                      draggedIndex === index ? 'opacity-50 scale-95' : 'hover:scale-105'
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="aspect-square relative overflow-hidden rounded-lg border-2 border-gray-200 hover:border-indigo-300">
                      <img
                        src={image.url}
                        alt="Uploaded"
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Ana Görsel Badge */}
                      {index === 0 ? (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md">
                          ⭐ Ana Görsel
                        </div>
                      ) : (
                        <div className="absolute top-2 left-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                          {index + 1}
                        </div>
                      )}

                      {/* Drag Indicator */}
                      <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-1 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        ⟡
                      </div>

                      {/* Controls Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex space-x-2">
                          {/* Quick Position Controls */}
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => moveImageUp(index)}
                              className="p-2 bg-white text-gray-700 rounded-full hover:bg-gray-100 shadow-md transition-all"
                              title="Öne al"
                            >
                              <ChevronUpIcon className="w-4 h-4" />
                            </button>
                          )}
                          {index < uploadedImages.length - 1 && (
                            <button
                              type="button"
                              onClick={() => moveImageDown(index)}
                              className="p-2 bg-white text-gray-700 rounded-full hover:bg-gray-100 shadow-md transition-all"
                              title="Arkaya al"
                            >
                              <ChevronDownIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeUploadedImage(index)}
                            className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-md transition-all"
                            title="Sil"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Image Info */}
                    <div className="mt-2 text-center">
                      <p className="text-xs text-gray-500">
                        {index === 0 ? 'Ana görsel' : `${index + 1}. görsel`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Instructions */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-2">
                  <div className="text-blue-600 mt-0.5">💡</div>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Nasıl sıralanır:</p>
                    <ul className="mt-1 space-y-1">
                      <li>• Görsel üzerine tıklayıp sürükleyerek yerini değiştirin</li>
                      <li>• Ok butonlarıyla tek tek hareket ettirin</li>
                      <li>• İlk sıradaki görsel otomatik olarak ana görsel olur</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">🛋️ Takım İçeriği</h2>
          
          {/* Add Product */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Ürün Ekle</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Ürün Seçin</option>
                  {products.filter(p => !setItems.find(item => item.product_id === p.id)).map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {formatCurrency(product.price)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <input
                  type="number"
                  min="1"
                  value={selectedQuantity}
                  onChange={(e) => setSelectedQuantity(e.target.value)}
                  placeholder="Adet"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={addSetItem}
                  disabled={!selectedProduct}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Ekle
                </button>
              </div>
            </div>
          </div>

          {/* Selected Products List */}
          {setItems.length > 0 ? (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900">Seçili Ürünler</h4>
              {setItems.map((item, index) => (
                <div key={item.product_id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                    <p className="text-sm text-gray-500">
                      Birim fiyat: {formatCurrency(item.product_price)}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Adet:</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(item.product_id, e.target.value)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                      />
                    </div>
                    
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(item.quantity * item.product_price)}
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => removeSetItem(item.product_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Price Summary */}
              <div className="mt-6 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-indigo-700">Toplam Ürün Fiyatı:</span>
                    <span className="font-medium text-indigo-900">{formatCurrency(calculateTotalPrice())}</span>
                  </div>
                  {formData.discount_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700">İndirim:</span>
                      <span className="font-medium text-green-900">-{formatCurrency(formData.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-indigo-200">
                    <span className="text-indigo-900">Final Takım Fiyatı:</span>
                    <span className="text-indigo-900">{formatCurrency(calculateDiscountedPrice())}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Henüz ürün eklenmedi</p>
              <p className="text-xs text-gray-400 mt-1">Yukarıdan ürün ekleyerek başlayın</p>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">💰 Fiyatlandırma</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Özel Takım Fiyatı (₺)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.set_price}
                onChange={(e) => setFormData({ ...formData, set_price: e.target.value })}
                placeholder="Otomatik hesaplanacak"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Boş bırakırsanız ürün fiyatları toplamından indirim düşülür
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                İndirim Tutarı (₺)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.discount_amount}
                onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/product-sets')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading || setItems.length === 0}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Oluşturuluyor...' : 'Takım Oluştur'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddProductSetPage 